import { computed, observable } from 'mobx'
import ModelStore from '../../../shared-lib/stores/ModelStore'
import { $RootStore } from '../../../src/stores/RootStore'

export const defaultData = {
  _id: '',
  firstName: '',
  lastName: '',
  emailAddress: '',
  mobile: '',
  tags: [],
  addresses: [],
  notes: '',
  color: '#CDDEE0',
  professionalId: null,
  isRedacted: false,
  deletedAt: null,
  gender: 'Private',
  birthday: null,
  stats: {
    acceptedOrCompletedBookings: 0,
    totalSpend: 0,
    averageSpend: 0,
    lastBookedDate: null,
    noShows: 0,
  },
  notifications: {
    emailCrm: true,
    smsCrm: true,
    emailUpdates: true,
    smsUpdates: true,
  },
}

export interface $ExternalClientAddress {
  placeId: string
  lat?: number
  lng?: number
  line1?: string
  line2: string
  region: string
  postalCode: string
  hideFullAddress?: boolean
  name?: string
  locationId?: string
  location?: GeoJsonPoint
  timezone?: string | null
}

type $ExternalClientModelBase = ExternalClientModel
export interface $ExternalClientModel extends $ExternalClientModelBase {
  addresses: Array<$ExternalClientAddress>
  gender: string,
}

export type ClientLoyaltyProgress = {
  progress: LoyaltyProgress
  scheme: LoyaltyScheme
}

export type ClientProgressPreviewProps = { scheme: Pick<ClientLoyaltyProgress['scheme'], 'goal' | 'goalNumber' | 'schemeUsage'>, progress: Pick<ClientLoyaltyProgress['progress'], 'current' | 'totalRedeems'> }

export default class ExternalClientModel extends ModelStore {
  @observable accessor data: ExternalClient = { ...defaultData }
  fetchMethodName = 'externalClient'
  removeMethodName = 'deleteExternalClient'
  saveMethodName = 'addOrUpdateExternalClient'

  constructor(data: Partial<ExternalClient>, rootStore: $RootStore) {
    super(data, rootStore)
    this.merge(data)
  }

  saveArgs = (): any => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { professionalId, stats, isRedacted, deletedAt, ...data } = this.data

    return {
      data: {
        ...data,
        professionalId: professionalId || this.rootStore.authStore.dataScopeId || undefined,
      },
    }
  }

  @computed get clientLoyaltySchemesProgress(): ClientLoyaltyProgress[] {
    return [
      this.clientLoyaltyProgress,
      this.clientReferralProgress,
    ].filter(Boolean) as ClientLoyaltyProgress[]
  }

  @computed get clientLoyaltyProgress(): ClientLoyaltyProgress | undefined {
    if (!this.rootStore.userStore.defaultLoyaltyScheme) return
    return this.clientProgressForScheme(this.rootStore.userStore.defaultLoyaltyScheme._id)
  }

  @computed get clientReferralProgress(): ClientLoyaltyProgress | undefined {
    if (!this.rootStore.userStore.clientReferralScheme) return
    return this.clientProgressForScheme(this.rootStore.userStore.clientReferralScheme._id)
  }

  clientProgressForScheme = (schemeId: string): ClientLoyaltyProgress | undefined => {
    const scheme = this.rootStore.userStore.loyaltySchemes?.find(s => s._id === schemeId)
    if (!scheme) return
    const progress = this.data.loyaltyProgress?.find(p => p.loyaltySchemeId === scheme._id)
    if (!progress) {
      if (scheme.status === 'active') {
        return { scheme, progress: { current: 0, totalRedeems: 0, loyaltySchemeId: scheme._id } }
      }
      return
    }

    return {
      progress,
      scheme: scheme,
    }
  }

  @computed get fullName() {
    return `${this.data.firstName} ${this.data.lastName}`
  }

  @computed get ownerId() {
    return this.data.professionalId
  }
}
